import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import React from 'react';
import { useMount } from 'react-use';

import { Icon } from '@eb/eds-icon';
import { ChevronDownChunky } from '@eb/eds-iconography';

import { CityBrowseHeaderImage } from './CityBrowseHeaderImage';
import { LocationAutocompleteContainer } from '../../../../containers/LocationAutocompleteContainer';

import './cityBrowseHeader.scss';

const ChevronIcon = (
    <Icon color="ui-blue--hover" size="medium" type={<ChevronDownChunky />} />
);

const CityBlurb = ({ cityBlurb }) => {
    let component = null;

    if (cityBlurb) {
        component = (
            // using dangerouslySetInnerHtml because data from wagtail is written in HTML
            <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: cityBlurb.toString() }}
                className="eds-text-bm eds-l-mar-vert-4 spaced-paragraphs"
                data-spec="city-blurb"
            />
        );
    }

    return component;
};

const isContentLongerThanInput = (proxyWidthElement, inputContainerElement) => {
    const proxyWidth = proxyWidthElement?.current.getBoundingClientRect().width;
    const containerWidth = inputContainerElement?.current.getBoundingClientRect()
        .width;

    return proxyWidth > containerWidth - 20;
};

export const useIsMultilineInput = () => {
    const [isMultiline, setIsMultiline] = React.useState(false);
    const proxyRef = React.useRef(null);
    const inputContainerRef = React.useRef(null);

    const debouncedListener = React.useCallback(() => {
        const checkNodes = () => {
            const shouldBeMultiline = isContentLongerThanInput(
                proxyRef,
                inputContainerRef,
            );

            if (shouldBeMultiline && !isMultiline) {
                setIsMultiline(true);
            } else if (!shouldBeMultiline && isMultiline) {
                setIsMultiline(false);
            }
        };

        const debouncedCheckNodes = debounce(checkNodes, 150);

        debouncedCheckNodes();
    }, [isMultiline]);

    React.useEffect(() => {
        isContentLongerThanInput(proxyRef, inputContainerRef) &&
            setIsMultiline(true);

        window.addEventListener('resize', debouncedListener);

        () => window.removeEventListener('resize', debouncedListener);
    }, [debouncedListener]);

    return { isMultiline, proxyRef, inputContainerRef };
};

export const CityBrowseHeader = (props) => {
    const [isMounted, setIsMounted] = React.useState(false);
    useMount(() => setIsMounted(true));

    const { isMultiline, proxyRef, inputContainerRef } = useIsMultilineInput();

    const {
        cityBlurb,
        cityImage,
        citySmallImage,
        shouldHideMask,
        experiments,
        currentPlace,
        h1Prefix,
        handleUseCurrentLocation,
        handleLocationSelect,
        history,
        isBotRequest,
    } = props;

    return (
        <header className="city-browse-header">
            <div className="city-browse-header__main eds-g-cell eds-g-cell-1-1 eds-g-cell-mn-4-12">
                {
                    // Deal with issue where this will briefly appear during the desktop load
                    isMounted && (
                        <div className="city-browse-header__aside city-browse-header__aside--small eds-g-cell eds-g-cell-1-1 eds-l-mar-top-2 eds-l-mar-bot-10">
                            <CityBrowseHeaderImage
                                currentPlace={currentPlace}
                                cityImage={cityImage}
                                citySmallImage={citySmallImage}
                                shouldHideMask={shouldHideMask}
                                experiments={experiments}
                            />
                        </div>
                    )
                }
                <div className="city-browse-header__main__content eds-align--center-vertical">
                    <div className="eds-g-cell eds-g-cell-1-1 eds-g-offset-mn-1-12 eds-g-cell--has-overflow eds-l-pad-hor-4">
                        <h1>
                            <span className="eds-text-bm eds-l-mar-bot-4">
                                {h1Prefix}{' '}
                            </span>
                            <span
                                ref={inputContainerRef}
                                className="city-browse-header__city-name-text"
                                data-current-text={currentPlace}
                                data-testid="city-browse-header"
                            >
                                {isBotRequest ? (
                                    currentPlace
                                ) : (
                                    <LocationAutocompleteContainer
                                        handleUseCurrentLocation={
                                            handleUseCurrentLocation
                                        }
                                        handleLocationSelect={
                                            handleLocationSelect
                                        }
                                        prefix={ChevronIcon}
                                        history={history}
                                        autocompleteProps={{
                                            borderType: 'content',
                                            preventInputValuePrefill: true,
                                            id: 'browse-location-selector',
                                            isMultiline,
                                        }}
                                    />
                                )}
                            </span>
                        </h1>
                        <div
                            className="city-browse-header__city-name-text--width-proxy"
                            ref={proxyRef}
                            style={{
                                position: 'absolute',
                                color: 'transparent',
                                pointerEvents: 'none',
                            }}
                        >
                            {currentPlace}
                        </div>
                        <CityBlurb cityBlurb={cityBlurb} />
                    </div>
                </div>
            </div>

            <aside className="city-browse-header__aside city-browse-header__aside--large eds-g-cell eds-g-cell-7-12 eds-g-offset-1-12 eds-l-mn-pad-left-2">
                <CityBrowseHeaderImage
                    currentPlace={currentPlace}
                    cityImage={cityImage}
                    citySmallImage={citySmallImage}
                    shouldHideMask={shouldHideMask}
                    experiments={experiments}
                />
            </aside>
        </header>
    );
};

CityBrowseHeader.propTypes = {
    currentPlace: PropTypes.string,
    cityBlurb: PropTypes.string,
    cityImage: PropTypes.string,
    citySmallImage: PropTypes.string,
    handleLocationSuggestionChange: PropTypes.func,
    shouldHideMask: PropTypes.bool,
    handleUseCurrentLocation: PropTypes.func,
    handleLocationSelect: PropTypes.func,
    h1: PropTypes.string,
};
